import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, ArrowRight, ArrowLeft, Loader2, CheckCircle, BarChart3, TrendingUp } from 'lucide-react';
import { mediaLiteracyScanApi, MediaLiteracyQuestion } from '../api/mediaLiteracyScan';

type ScanState = 'intro' | 'scan' | 'submitting' | 'results';

export const MediaLiteracyScan: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<ScanState>('intro');
  const [questions, setQuestions] = useState<MediaLiteracyQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [attemptId, setAttemptId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const data = await mediaLiteracyScanApi.getQuestions();
      setQuestions(data);
    } catch (err: any) {
      setError('Kon vragen niet laden. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startScan = async () => {
    try {
      setLoading(true);
      
      // Generate a temporary user ID for anonymous users
      const tempUserId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const attempt = await mediaLiteracyScanApi.createAttempt(tempUserId);
      setAttemptId(attempt.id);
      setState('scan');
    } catch (err: any) {
      setError('Kon scan niet starten. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (rating: number) => {
    if (!currentQuestion) return;
    
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: rating
    }));
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      submitScan();
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const submitScan = async () => {
    try {
      setState('submitting');
      setLoading(true);
      
      await mediaLiteracyScanApi.updateAttempt(attemptId, answers);
      setState('results');
    } catch (err: any) {
      setError('Kon scan niet opslaan. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreLevel = (score: number) => {
    if (score >= 4.5) return { level: 'Expert', color: 'text-green-600', bgColor: 'bg-green-50' };
    if (score >= 3.5) return { level: 'Gevorderd', color: 'text-blue-600', bgColor: 'bg-blue-50' };
    if (score >= 2.5) return { level: 'Gemiddeld', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    if (score >= 1.5) return { level: 'Basis', color: 'text-orange-600', bgColor: 'bg-orange-50' };
    return { level: 'Beginner', color: 'text-red-600', bgColor: 'bg-red-50' };
  };

  const getCategoryScore = (category: string) => {
    const categoryQuestions = questions.filter(q => q.category === category);
    const categoryAnswers = categoryQuestions
      .map(q => answers[q.id])
      .filter(answer => answer !== undefined);
    
    if (categoryAnswers.length === 0) return 0;
    return categoryAnswers.reduce((sum, answer) => sum + answer, 0) / categoryAnswers.length;
  };

  const getOverallScore = () => {
    const allAnswers = Object.values(answers);
    if (allAnswers.length === 0) return 0;
    return allAnswers.reduce((sum, answer) => sum + answer, 0) / allAnswers.length;
  };

  const categories = Array.from(new Set(questions.map(q => q.category)));

  if (loading && state === 'intro') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-600">Vragen laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-800 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Opnieuw proberen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mediawijsheid Geletterdheid Scan</h1>
              <p className="text-gray-600">Evalueer je mediawijsheid en ontdek waar je kunt verbeteren</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {state === 'intro' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-8">
              <Newspaper className="w-16 h-16 text-purple-600 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Welkom bij de Mediawijsheid Scan
              </h2>
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                Deze scan helpt je om je huidige niveau van mediawijsheid te beoordelen. 
                Je krijgt 18 vragen over verschillende aspecten van mediawijsheid.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="font-semibold text-purple-900 mb-3">Wat wordt getest?</h3>
                <ul className="space-y-2 text-purple-800">
                  <li>• Bronbeoordeling</li>
                  <li>• Nepnieuws herkenning</li>
                  <li>• Algoritmes & filterbubbels</li>
                  <li>• Psychologie & manipulatie</li>
                  <li>• Fact-checking & verificatie</li>
                  <li>• Verantwoordelijk gebruik</li>
                </ul>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="font-semibold text-green-900 mb-3">Hoe werkt het?</h3>
                <ul className="space-y-2 text-green-800">
                  <li>• 18 vragen met 5-puntsschaal</li>
                  <li>• Duurt ongeveer 10-15 minuten</li>
                  <li>• Directe feedback per categorie</li>
                  <li>• Persoonlijke aanbevelingen</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 mb-8 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">
                Gebaseerd op Mediawijsheid.nl
              </h3>
              <p className="text-blue-800 text-sm">
                Deze scan is gebaseerd op informatie en richtlijnen van het Netwerk Mediawijsheid. 
                Geef bij elke vraag eerlijk aan in hoeverre deze uitspraak op jou van toepassing is.
              </p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={startScan}
                disabled={loading}
                className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Laden...
                  </>
                ) : (
                  <>
                    Start Scan
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Terug
              </button>
            </div>
          </div>
        )}

        {state === 'scan' && currentQuestion && (
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Vraag {currentQuestionIndex + 1} van {questions.length}
                </span>
                <span className="text-sm font-medium text-gray-700">
                  {Math.round(progress)}% voltooid
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium mb-4">
                  {currentQuestion.category}
                </span>
                <h2 className="text-xl font-bold text-gray-900 mb-3">
                  {currentQuestion.question}
                </h2>
                {currentQuestion.description && (
                  <p className="text-gray-600 text-sm bg-gray-50 rounded-lg p-4">
                    {currentQuestion.description}
                  </p>
                )}
              </div>

              <div className="mb-8">
                <p className="text-center text-sm text-gray-600 mb-4">
                  Hoe bekend ben je met dit onderwerp?
                </p>
                <div className="grid grid-cols-5 gap-3">
                  {[1, 2, 3, 4, 5].map((rating) => {
                    const isSelected = answers[currentQuestion.id] === rating;
                    return (
                      <button
                        key={rating}
                        onClick={() => handleAnswer(rating)}
                        className={`relative p-4 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-center">
                          <div className={`text-2xl font-bold mb-1 ${
                            isSelected ? 'text-purple-600' : 'text-gray-400'
                          }`}>
                            {rating}
                          </div>
                          {isSelected && (
                            <CheckCircle className="w-5 h-5 text-purple-600 mx-auto" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-2 px-1">
                  <span>Helemaal niet</span>
                  <span>Enigszins</span>
                  <span>Redelijk</span>
                  <span>Goed</span>
                  <span>Zeer</span>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <button
                  onClick={previousQuestion}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Vorige
                </button>

                <button
                  onClick={nextQuestion}
                  disabled={!answers[currentQuestion.id]}
                  className="flex items-center px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {currentQuestionIndex === questions.length - 1 ? 'Voltooien' : 'Volgende'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}

        {state === 'submitting' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
              <p className="text-lg text-gray-700 font-medium">Resultaten berekenen...</p>
            </div>
          </div>
        )}

        {state === 'results' && (
          <div className="space-y-6">
            {/* Overall Score Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full ${getScoreLevel(getOverallScore()).bgColor} mb-4`}>
                  <span className={`text-4xl font-bold ${getScoreLevel(getOverallScore()).color}`}>
                    {getOverallScore().toFixed(1)}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Jouw Mediawijsheid Score
                </h2>
                <p className="text-lg text-gray-600 mb-4">
                  Niveau: <span className={`font-semibold ${getScoreLevel(getOverallScore()).color}`}>
                    {getScoreLevel(getOverallScore()).level}
                  </span>
                </p>
                <div className="flex justify-center gap-8 mt-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">
                      {getOverallScore().toFixed(1)}
                    </div>
                    <div className="text-sm text-gray-600">Gemiddelde Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">
                      {questions.length}
                    </div>
                    <div className="text-sm text-gray-600">Vragen</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${getScoreLevel(getOverallScore()).color}`}>
                      {getScoreLevel(getOverallScore()).level}
                    </div>
                    <div className="text-sm text-gray-600">Niveau</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Resultaten per Categorie
              </h3>
              
              <div className="grid gap-4">
                {categories.map((category) => {
                  const score = getCategoryScore(category);
                  const level = getScoreLevel(score);
                  
                  return (
                    <div key={category} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{category}</h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                            <div 
                              className={`h-2 rounded-full transition-all duration-500 ${level.bgColor.replace('bg-', 'bg-').replace('-50', '-500')}`}
                              style={{ width: `${(score / 5) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-600">
                            {score.toFixed(1)}
                          </span>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${level.bgColor} ${level.color}`}>
                        {level.level}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Aanbevelingen
              </h3>
              
              <div className="space-y-4">
                {categories.map((category) => {
                  const score = getCategoryScore(category);
                  
                  if (score < 3) {
                    return (
                      <div key={category} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <h4 className="font-medium text-orange-900 mb-2">
                          {category} - Verbetering Aanbevolen
                        </h4>
                        <p className="text-orange-800 text-sm">
                          Je score van {score.toFixed(1)} toont dat er ruimte is voor verbetering in deze categorie. 
                          Overweeg om de Mediawijsheid Kennistoets te doen of de training te volgen.
                        </p>
                      </div>
                    );
                  } else if (score >= 4) {
                    return (
                      <div key={category} className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-medium text-green-900 mb-2">
                          {category} - Sterke Kennis
                        </h4>
                        <p className="text-green-800 text-sm">
                          Uitstekend! Je score van {score.toFixed(1)} toont dat je sterke kennis hebt in deze categorie. 
                          Je zou anderen kunnen helpen met mediawijsheid.
                        </p>
                      </div>
                    );
                  }
                  return null;
                })}
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">
                    Meer leren over mediawijsheid?
                  </h4>
                  <p className="text-blue-800 text-sm">
                    Bezoek <a href="https://www.mediawijsheid.nl/nepnieuws/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Mediawijsheid.nl</a> voor meer informatie en handige tips.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Wat wil je nu doen?
              </h3>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/media-wisdom-assessment')}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                >
                  Mediawijsheid Kennistoets
                </button>
                <button
                  onClick={() => navigate('/topics?theme=mediawijsheid')}
                  className="bg-white border-2 border-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Mediawijsheid Training
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-white border-2 border-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Opnieuw Doen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
